import { useId } from "react";
import type { InventionElementResponse } from "@patent-studio/api-types";
import { HoverTip } from "@/components/ui/HoverTip";
import { CAPTION_LINE, LINK_MARK } from "@/components/ui/marks";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Select } from "@/components/ui/select";
import { StatusPill } from "@/components/ui/StatusPill";
import { REFERENCE_TINT } from "@/components/ui/StructuredText";
import { cn } from "@/lib/utils";
import { SpanField, called, type WordsMark } from "./terms";

/** The element a relation names, where it is still in the set. */
function targetOf(
  element: InventionElementResponse,
  elements: readonly InventionElementResponse[],
): InventionElementResponse | undefined {
  const targetId = element.relation?.target_id;
  return elements.find((other) => other.element_id === targetId);
}

/**
 * The words that say how the element attaches, marked in its text as the link
 * they are, with the element they attach to named on hover or focus.
 */
function RelationLink({
  words,
  target,
}: {
  words: string;
  target: InventionElementResponse;
}) {
  return (
    <HoverTip
      tone="dark"
      tip={
        <span>
          Attaches to {called(target)}
          {target.number !== null && `: ${target.text}`}
        </span>
      }
    >
      {({ triggerProps }) => (
        <span
          {...triggerProps}
          tabIndex={0}
          data-relation={target.element_id}
          className={cn(REFERENCE_TINT, LINK_MARK)}
        >
          {words}
        </span>
      )}
    </HoverTip>
  );
}

/** The relation as a mark on the element's words, where its words are in them. */
export function relationMark(
  element: InventionElementResponse,
  elements: readonly InventionElementResponse[],
): WordsMark | null {
  const relation = element.relation;
  const target = targetOf(element, elements);
  if (
    relation === null ||
    target === undefined ||
    relation.start === null ||
    relation.end === null
  )
    return null;
  return {
    start: relation.start,
    end: relation.end,
    draw: (words) => <RelationLink words={words} target={target} />,
  };
}

/**
 * What the element attaches to, said beneath its words where no link is drawn
 * in them: its words overlap the term's, or are not words of its text at all.
 * Words of the attorney's own save with their finding, marked as a term of
 * theirs is marked.
 */
export function AttachesTo({
  element,
  elements,
  linked,
}: {
  element: InventionElementResponse;
  elements: readonly InventionElementResponse[];
  /** Whether the link is drawn in the words, which then says all this does. */
  linked: boolean;
}) {
  const relation = element.relation;
  const target = targetOf(element, elements);
  if (relation === null || target === undefined || linked) return null;
  return (
    <p className={cn("flex flex-wrap items-center gap-1.5", CAPTION_LINE)}>
      <span>
        Attaches to {called(target)} by &ldquo;{relation.words}&rdquo;
      </span>
      {relation.not_a_span && (
        <StatusPill tone="warning" size="sm" plain>
          Not in the words
        </StatusPill>
      )}
    </p>
  );
}

/** The relation as the editor holds it: the element chosen, and the words marked. */
export interface RelationDraft {
  targetId: string;
  words: string;
}

/** The relation the element carries, as the editor starts from it. */
export function relationDraftOf(
  element: InventionElementResponse,
): RelationDraft {
  return {
    targetId: element.relation?.target_id ?? "",
    words: element.relation?.words ?? "",
  };
}

/**
 * Whether the draft attaches the element to another with no words that say
 * how: none marked, and none held to keep. Such a draft has nothing to write,
 * so the editor holds Save rather than drop the choice.
 */
export function relationUnmarked(
  element: InventionElementResponse,
  draft: RelationDraft,
): boolean {
  return (
    draft.targetId !== "" &&
    draft.words === "" &&
    (element.relation?.words ?? "") === ""
  );
}

/**
 * The write a draft asks for against the relation the element carries: none
 * where nothing changed, a clear where it now attaches to nothing, and a
 * relation where it attaches to an element by words. Words left unmarked keep
 * the words it had.
 */
export function relationWrite(
  element: InventionElementResponse,
  draft: RelationDraft,
): { clear: true } | { targetId: string; words: string } | null {
  const held = element.relation;
  if (draft.targetId === "") return held === null ? null : { clear: true };
  const words = draft.words === "" ? (held?.words ?? "") : draft.words;
  if (words === "") return null;
  if (held?.target_id === draft.targetId && held.words === words) return null;
  return { targetId: draft.targetId, words };
}

/**
 * The editor's part for the relation: the element this one attaches to,
 * chosen from the others in the set, and the words of its text that say how,
 * marked as a term is marked.
 */
export function RelationField({
  element,
  elements,
  text,
  draft,
  onChange,
}: {
  element: InventionElementResponse;
  elements: readonly InventionElementResponse[];
  /** The words being written, whose spans are offered. */
  text: string;
  draft: RelationDraft;
  onChange: (draft: RelationDraft) => void;
}) {
  const id = useId();
  const others = elements.filter(
    (other) => other.element_id !== element.element_id,
  );
  return (
    <div className="mt-2">
      <MicroLabel as="label" size="sm" htmlFor={id} className="block">
        Attaches to
      </MicroLabel>
      <Select
        size="dense"
        id={id}
        value={draft.targetId}
        onChange={(event) => {
          const targetId = event.target.value;
          // Attached to nothing, the words go too: the field is gone, and
          // words it no longer shows must not be sent when a target comes back.
          onChange(
            targetId === "" ? { targetId, words: "" } : { ...draft, targetId },
          );
        }}
        className="mt-1"
      >
        <option value="">Nothing</option>
        {others.map((other) => (
          <option key={other.element_id} value={other.element_id}>
            {other.number === null
              ? `Unnumbered: ${other.text}`
              : `Element ${String(other.number)}: ${other.text}`}
          </option>
        ))}
      </Select>
      {draft.targetId !== "" && (
        <SpanField
          caption="Mark the words that say how"
          label="Relation words"
          text={text}
          optional={element.relation !== null}
          onChange={(words) => {
            onChange({ ...draft, words });
          }}
        />
      )}
    </div>
  );
}
